// The vehicle register: search, filters and the list (2.2.1). Opens the vehicle card.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { sv } from '@aldorock-fordon/shared/sv';
import type { VehicleList, VehicleType, VehicleStatus } from '@aldorock-fordon/shared/api-types';
import { api, type Me } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { Table } from '../ui/Table.tsx';
import { VehicleStatusView } from '../ui/Status.tsx';
import { num, plate } from '../format.ts';

type Unit = { id: string; name: string; kind: string; path: string; parentId: string | null };

export function VehiclesPage({ me }: { me: Me }) {
  const h1 = usePage(sv.vehicles.title);
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const type = params.get('type') ?? '';
  const status = params.get('status') ?? '';
  const unit = params.get('unit') ?? '';
  const [draft, setDraft] = useState(q);
  const [limit, setLimit] = useState(100);
  const query = new URLSearchParams();
  if (q) query.set('q', q);
  if (type) query.set('type', type);
  if (status) query.set('status', status);
  if (unit) query.set('unit', unit);
  query.set('limit', String(limit));
  const list = useQuery({
    queryKey: ['vehicles', query.toString()],
    queryFn: () => api<VehicleList>(`/vehicles?${query}`),
  });
  const units = useQuery({ queryKey: ['units'], queryFn: () => api<Unit[]>('/vehicles/units') });
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };
  const d = list.data;
  return (
    <>
      <PageHead
        help="vehicles"
        title={sv.vehicles.title}
        titleRef={h1}
        actions={
          me.permissions['vehicle.write'] ? (
            <Link className="btn btn-primary" to="/fordon/nytt">
              {sv.vehicles.newVehicle}
            </Link>
          ) : null
        }
      />
      <p>{sv.vehicles.intro}</p>
      <form
        className="filter-row"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          set('q', draft.trim());
        }}
      >
        <div className="field">
          <label className="label" htmlFor="q">
            {sv.vehicles.searchLabel}
          </label>
          <span className="help" id="q-help">
            {sv.vehicles.searchHelp}
          </span>
          <div className="input-row">
            <input
              className="input"
              id="q"
              name="q"
              value={draft}
              aria-describedby="q-help"
              onChange={(e) => setDraft(e.target.value)}
            />
            <button className="btn" type="submit">
              {sv.vehicles.search}
            </button>
          </div>
        </div>
        <div className="field">
          <label className="label" htmlFor="type">
            {sv.vehicles.typeFilter}
          </label>
          <div className="select">
            <select id="type" value={type} onChange={(e) => set('type', e.target.value)}>
              <option value="">{sv.vehicles.all}</option>
              {(Object.keys(sv.vehicleTypes) as VehicleType[]).map((t) => (
                <option key={t} value={t}>
                  {sv.vehicleTypes[t]}
                  {d?.counts.byType[t] ? ` (${d.counts.byType[t]})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label className="label" htmlFor="status">
            {sv.vehicles.statusFilter}
          </label>
          <div className="select">
            <select id="status" value={status} onChange={(e) => set('status', e.target.value)}>
              <option value="">{sv.vehicles.all}</option>
              {(Object.keys(sv.vehicleStatus) as VehicleStatus[]).map((s) => (
                <option key={s} value={s}>
                  {sv.vehicleStatus[s]}
                  {d?.counts.byStatus[s] ? ` (${d.counts.byStatus[s]})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label className="label" htmlFor="unit">
            {sv.vehicles.unitFilter}
          </label>
          <div className="select">
            <select id="unit" value={unit} onChange={(e) => set('unit', e.target.value)}>
              <option value="">{sv.vehicles.all}</option>
              {units.data
                ?.filter((u) => u.kind === 'forvaltning')
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
            </select>
          </div>
        </div>
      </form>
      {list.isPending ? <p role="status">{sv.app.loading}</p> : null}
      {d ? (
        <>
          <p role="status" className="muted">
            {sv.vehicles.results(d.items.length, d.total)}
          </p>
          <Table
            caption={sv.vehicles.listCaption}
            id="fordon"
            isEmpty={d.items.length === 0}
            empty={sv.vehicles.empty}
            hasOptional
          >
            <thead>
              <tr>
                <th scope="col">{sv.columns.registration}</th>
                <th scope="col">{sv.columns.vehicle}</th>
                <th scope="col" className="col-opt">
                  {sv.columns.type}
                </th>
                <th scope="col" className="col-opt">
                  {sv.columns.fuel}
                </th>
                <th scope="col">{sv.columns.forvaltning}</th>
                <th scope="col" className="col-opt">
                  {sv.columns.unit}
                </th>
                <th scope="col" className="r col-opt">
                  {sv.columns.odometer}
                </th>
                <th scope="col" className="col-opt">
                  {sv.columns.inspectionDue}
                </th>
                <th scope="col">{sv.columns.status}</th>
              </tr>
            </thead>
            <tbody>
              {d.items.map((v) => (
                <tr key={v.id}>
                  <td className="num">
                    <Link to={`/fordon/${v.id}`}>{plate(v.registrationNumber)}</Link>
                  </td>
                  <td>
                    {[v.make, v.model].filter(Boolean).join(' ')}
                    {v.modelYear ? <span className="muted"> {v.modelYear}</span> : null}
                  </td>
                  <td className="col-opt">{sv.vehicleTypes[v.vehicleType]}</td>
                  <td className="col-opt">{v.fuel ? sv.fuels[v.fuel] : ''}</td>
                  <td>{v.forvaltning ?? ''}</td>
                  <td className="col-opt">{v.unit ?? ''}</td>
                  <td className="r col-opt">{v.odometerKm != null ? num(v.odometerKm) : ''}</td>
                  <td className="col-opt num">{v.inspectionDue ?? ''}</td>
                  <td>
                    <VehicleStatusView status={v.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {d.items.length < d.total ? (
            <p className="actions-inline">
              <button className="btn" type="button" onClick={() => setLimit((l) => l + 100)}>
                {sv.vehicles.showMore(Math.min(100, d.total - d.items.length))}
              </button>
            </p>
          ) : null}
        </>
      ) : null}
    </>
  );
}
